// Vendor Libs
import React from 'react';
import PropTypes from 'prop-types';
import {find, get, startCase} from 'lodash';

// Components
import DateTime from '../core/date-time';
import Form from '../core/form';
import FormButtons from '../core/form-buttons';
import Input from '../core/input';
import Permissions from '../core/permissions';
import Select from '../core/select';

// Core Libs
import {parseDate} from '../../lib/core/date';

// Styles
import './mediaitem-offerings-form.scss';

function MediaItemOfferingsForm({
  currentOffering: { period } = {},
  editing,
  formName,
  offerings,
  onCancelClick,
  serviceName,
}) {
  const getFieldProps = (name, fieldType) => ({
    className: fieldType === 'select' ? 'mediaitem-offerings-form--select' : 'mediaitem-offerings-form--input',
    label: startCase(name),
    name,
    ...getCustomFieldProps(name),
  });

  const getCustomFieldProps = (fieldName) => {
    const customFieldProps = {
      endDate: {
        showTime: true,
        showTimezone: true,
        validate: ['required', 'date', validateEndDate],
        onlyShowFirstError: true,
        parseFormat: 'YYYY-MM-DDTHH:mm:ssZ',
      },
      rentalDuration: {
        label: 'Rental Duration (hours)',
        validate: ['numeric', 'nonNegativeNumber', validateRental],
      },
      resourceId: {
        validate: [
          'required',
          (formData, name, value) => validateResourceId(formData, name, value, editing, offerings),
        ],
      },
      period: {
        defaultValue: period !== undefined && period !== null ? `${period}` : '30',
        label: 'Period (days)',
        validate: ['numeric', 'nonNegativeNumber', validateRental, validatePeriod],
        visible: shouldRenderEditRecordField,
      },
      price: {
        label: 'Price (USD)',
        validate: ['price', 'nonNegativeNumber', validateRental],
      },
      releaseType: {
        title: 'Release Type Examples: Pre-Theatrical, Theatrical, New Release, DTV, Library',
      },
      startDate: {
        showTime: true,
        showTimezone: true,
        validate: ['required', 'date'],
        onlyShowFirstError: true,
        parseFormat: 'YYYY-MM-DDTHH:mm:ssZ',
      },
      streetDate: {
        validate: ['date'],
        onlyShowFirstError: true,
        parseFormat: 'YYYY-MM-DD',
      },
      threeDFormat: {
        label: '3D Format',
        validate: ['required'],
        visible: shouldRenderEditRecordField,
      },
      type: {
        validate: ['required'],
      },
    };

    return customFieldProps[fieldName];
  };

  const shouldRenderEditRecordField = (formData, name) => (name === 'threeDFormat'
            ? get(formData, 'resolution') === '3D'
            : name === 'period'
              ? serviceName === 'SlingTV' && get(formData, 'type') === 'TVOD'
        : false);

  const validateEndDate = (formData, _name, value) => {
    const startDate = parseDate(get(formData, 'startDate'));
    const endDate = parseDate(value);

    return startDate && endDate && startDate >= endDate ? 'End Date must be after Start Date' : undefined;
  };

  const validatePeriod = (_formData, _name, value) => (+value > 65000 ? 'Period has a max size of 65000' : undefined);

  const validateRental = (formData, _name, value) => (get(formData, 'type') === 'TVOD' && (value === '' || value === undefined)
            ? 'This field cannot be empty when Type is TVOD (Rental)'
            : undefined);

  const validateResourceId = (_formData, _name, value, editing, offerings) => !editing && find(offerings, { resourceId: value }) ? 'Resource ID must be unique' : undefined;

  const getFormProps = () => ({
    editing: editing,
    name: formName,
    fixedPositionedResponse: true,
    className: 'mediaitem-offerings-form',
    onReset: onCancelClick,
    resetOnSubmit: !editing,
    submitData: true,
  });

  return (
        <Permissions accessRequirement="write" notAuthorizedMessage={true} requiresServiceAccess={true}>
            <Form {...getFormProps()}>
                <Input {...getFieldProps('resourceId')} />
                <Select {...getFieldProps('type', 'select')}>
                    <option value="">Select a Type</option>
                    <option value="SVOD">SVOD - Subscription</option>
                    <option value="TVOD">TVOD - Rental</option>
                    <option value="FVOD">FVOD - Free</option>
                    <option value="FREE">FREE</option>
                    <option value="EST">EST - Purchase</option>
                </Select>
                <Input {...getFieldProps('releaseType')} />
                <Select {...getFieldProps('platform', 'select')}>
                    <option value="">None</option>
                    <option value="all">All</option>
                </Select>
                <Select {...getFieldProps('resolution', 'select')}>
                    <option value="">None</option>
                    <option value="SD">SD</option>
                    <option value="HD">HD (720p/1080i)</option>
                    <option value="1080p">1080p</option>
                    <option value="3D">3D</option>
                    <option value="4k">4k</option>
                </Select>
                <Select {...getFieldProps('threeDFormat', 'select')}>
                    <option value="">Select a 3D Format</option>
                    <option value="none">None</option>
                    <option value="side-by-side-half">Side-by-Side-Half</option>
                    <option value="side-by-side-full">Side-by-Side-Full</option>
                    <option value="top-bottom">Top-Bottom</option>
                </Select>
                <Input {...getFieldProps('sku')} />
                <DateTime {...getFieldProps('startDate')} />
                <DateTime {...getFieldProps('endDate')} />
                <DateTime {...getFieldProps('streetDate')} />
                <Input {...getFieldProps('rentalDuration')} />
                <Input {...getFieldProps('period')} />
                <Input {...getFieldProps('price')} />
                <FormButtons cancel={true} formName={formName} type="save" />
            </Form>
        </Permissions>
  );
}

MediaItemOfferingsForm.propTypes = {
    currentOffering: PropTypes.object,
    editing: PropTypes.bool,
    formName: PropTypes.string.isRequired,
    offerings: PropTypes.array,
    onCancelClick: PropTypes.func,
    serviceName: PropTypes.string.isRequired,
};

export default MediaItemOfferingsForm;
